import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/logo.png'; // Make sure this path is correct
import { Button } from "./ui/button.jsx"; // Import Button

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();

  // Reset scroll to top on page change
  useEffect(() => {
    if (!isOpen) {
      window.scrollTo(0, 0);
    }
  }, [pathname, isOpen]);

  const navLinks = [
    { title: 'Amama Bypass', path: '/platform#bypass' },
    { title: 'Portfolio', path: '/portfolio' },
    { title: 'Focus', path: '/focus' },
    { title: 'Team', path: '/team' },
    { title: 'For Founders', path: '/for-founders' },
    { title: 'Insights', path: '/insights' },
    { title: 'Contact', path: '/contact' },
  ];

  const mobileMenuVariant = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
    exit: { opacity: 0 },
  };

  const mobileLinkVariant = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <motion.header
      className="sticky top-0 w-full bg-background/70 backdrop-blur-lg z-50 border-b border-border/80 py-3"
    >
      <div className="container mx-auto px-6 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-3 z-50" onClick={() => setIsOpen(false)}>
          <img src={logo} alt="Amama Ventures Logo" className="h-10 md:h-12" />
        </Link>

        {/* --- Desktop Nav --- */}
        <nav className="hidden lg:flex items-center gap-10">
          {/* Make the UL items-center to vertically align Button with NavLinks */}
          <ul className="flex items-center gap-8">
            {navLinks.map((link) => (
              <li key={link.title}>
                {link.title === 'Contact' ? (
                  <Link to={link.path}>
                    {/* Use Button component with "hero" variant */}
                    <Button
                      variant="hero" // Changed variant
                      size="sm"      // Keep size small for navbar
                      // Adjust padding for navbar height if needed, remove blend mode
                      className="font-medium text-sm px-4 py-1.5"
                    >
                      {link.title}
                    </Button>
                  </Link>
                ) : (
                  <NavLink
                    to={link.path}
                    // Removed mix-blend-difference, explicitly set text color/opacity
                    className={({ isActive }) =>
                      `font-medium text-sm transition-opacity relative group text-black ${isActive ? 'opacity-100' : 'opacity-80 hover:opacity-100'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {link.title}
                        {/* Underline uses BLUE GRADIENT now */}
                        <span className={`absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300 group-hover:w-full ${isActive ? 'w-full' : 'w-0'}`}></span>
                      </>
                    )}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* --- Mobile Menu Button (Keeps blend mode) --- */}
        <button
          className="lg:hidden z-50 flex flex-col gap-[5px] w-8 h-8 justify-center"
          onClick={() => setIsOpen(!isOpen)}
        >
          <motion.span
            className="block h-0.5 w-full bg-black mix-blend-difference"
            animate={{ rotate: isOpen ? 45 : 0, y: isOpen ? 5.5 : 0 }}
            transition={{ duration: 0.2 }}
          ></motion.span>
          <motion.span
            className="block h-0.5 w-full bg-black mix-blend-difference"
            animate={{ opacity: isOpen ? 0 : 1 }}
            transition={{ duration: 0.1 }}
          ></motion.span>
          <motion.span
            className="block h-0.5 w-full bg-black mix-blend-difference"
            animate={{ rotate: isOpen ? -45 : 0, y: isOpen ? -5.5 : 0 }}
            transition={{ duration: 0.2 }}
          ></motion.span>
        </button>

        {/* --- Mobile Menu --- */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="lg:hidden absolute top-0 left-0 w-full h-screen bg-white/90 backdrop-blur-8xl z-40 flex flex-col items-center justify-center pt-24" variants={mobileMenuVariant}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              <nav>
                <ul className="flex flex-col items-center gap-10">
                  {navLinks.map((link) => (
                    <motion.li key={link.title} variants={mobileLinkVariant}>
                      <NavLink
                        to={link.path}
                        onClick={() => setIsOpen(false)}
                        className={({ isActive }) =>
                          `text-3xl font-bold transition-colors hover:text-secondary ${isActive ? 'text-secondary' : 'text-foreground'
                          }`
                        }
                      >
                        {link.title}
                      </NavLink>
                    </motion.li>
                  ))}
                </ul>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Navbar;